import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, Users, BrainCircuit, MessagesSquare, Settings, LogOut, ExternalLink, Network, Megaphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/site/Navbar";
import PublishBar from "./PublishBar";

const NAV = [
  ["/admin", "Overview", LayoutDashboard, true],
  ["/admin/konten", "Manajemen Konten", FileText],
  ["/admin/leads", "Lead & Inquiry", Users],
  ["/admin/knowledge-base", "AI Knowledge Base", BrainCircuit],
  ["/admin/chatbot", "Chatbot Analytics", MessagesSquare],
  ["/admin/pengaturan", "Profil Perusahaan", Settings],
  ["/admin/arsitektur", "Arsitektur Sistem", Network],
  ["/admin/campaign", "Campaign Blasting", Megaphone, false, "Marketing Tools"],
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="flex min-h-screen bg-[#F1F5F9]">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col bg-[#0B132B] text-slate-300 md:flex" data-testid="admin-sidebar">
        <div className="border-b border-white/10 p-5"><Logo dark /></div>
        <nav className="flex-1 space-y-1 p-3">
          {NAV.map(([to, label, Icon, end, group]) => (
            <div key={to}>{group && <p className="px-3 pb-1 pt-4 font-mono text-[10px] uppercase tracking-widest text-slate-500">{group}</p>}
            <NavLink key={to} to={to} end={end} data-testid={`admin-nav-${to.split("/").pop() || "overview"}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${isActive ? "border-l-2 border-[#D97706] bg-white/10 text-white" : "border-l-2 border-transparent hover:bg-white/5 hover:text-white"}`}>
              <Icon size={16} /> {label}
            </NavLink></div>
          ))}
        </nav>
        <div className="space-y-2 border-t border-white/10 p-4 text-xs">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white"><ExternalLink size={12} /> Lihat Website</a>
          <p className="truncate font-mono text-slate-500">{user?.email}</p>
          <button onClick={async () => { await logout(); nav("/admin/login"); }} data-testid="admin-logout-button" className="flex items-center gap-2 text-rose-300 hover:text-rose-200"><LogOut size={12} /> Keluar</button>
        </div>
      </aside>
      <div className="flex-1 overflow-x-hidden">
        <div className="flex gap-2 overflow-x-auto bg-[#0B132B] p-2 md:hidden">
          {NAV.map(([to, label, , end]) => <NavLink key={to} to={to} end={end} className={({ isActive }) => `whitespace-nowrap px-3 py-1.5 text-xs ${isActive ? "bg-[#D97706] text-white" : "text-slate-300"}`}>{label}</NavLink>)}
        </div>
        <PublishBar />
        <main className="p-4 sm:p-8"><Outlet /></main>
      </div>
    </div>
  );
}

export const PageHead = ({ eyebrow, title, desc, children }) => (
  <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
    <div>
      <p className="eyebrow mb-2">{eyebrow}</p>
      <h1 className="text-2xl font-bold tracking-tight text-[#0F2B48] sm:text-3xl">{title}</h1>
      {desc && <p className="mt-1 max-w-2xl text-sm text-slate-500">{desc}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Stat = ({ label, value, accent, testid }) => (
  <div className="border border-slate-200 bg-white p-5" data-testid={testid}>
    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`mt-2 text-3xl font-extrabold ${accent || "text-[#0F2B48]"}`}>{value}</p>
  </div>
);

export const Panel = ({ title, children, className = "", action }) => (
  <section className={`border border-slate-200 bg-white ${className}`}>
    {title && <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3"><h3 className="text-sm font-bold text-[#0F2B48]">{title}</h3>{action}</div>}
    <div className="p-5">{children}</div>
  </section>
);
